import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { listAdminProducts } from '@/services/products';
import { listAdminPlans } from '@/services/plans';
import { listAdminCustomers } from '@/services/users';
import { generateAdminLicense, type GeneratedLicense } from '@/services/licenses';
import type { Product, ProductPlan, LicenseStatus } from '@/domain/types';
import type { AdminCustomer } from '@/services/users';

function calcExpiry(durationDays: number | null): string {
  if (!durationDays) return '';
  const d = new Date();
  d.setDate(d.getDate() + durationDays);
  return d.toISOString().slice(0, 10);
}

export default function AdminLicenseGenerate() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [products, setProducts] = useState<Product[]>([]);
  const [plans, setPlans] = useState<ProductPlan[]>([]);
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [productId, setProductId] = useState('');
  const [planId, setPlanId] = useState('');
  const [userId, setUserId] = useState('');
  const [siteLimit, setSiteLimit] = useState('1');
  const [expiresAt, setExpiresAt] = useState('');
  const [status, setStatus] = useState<LicenseStatus>('active');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState<GeneratedLicense | null>(null);
  const [copied, setCopied] = useState(false);

  const selectedPlan = plans.find((p) => p.id === planId) ?? null;
  const siteLimitNum = Number(siteLimit) || 1;
  const planMaxSites = selectedPlan?.siteLimit ?? null;
  const siteOverPlan = planMaxSites !== null && planMaxSites !== 999 && siteLimitNum > planMaxSites;

  // Load products + customers + plans on mount
  useEffect(() => {
    setLoadingOptions(true);
    Promise.all([listAdminProducts(), listAdminCustomers(), listAdminPlans()])
      .then(([prods, custs, allPlans]) => {
        setProducts(prods);
        setCustomers(custs);
        setPlans(allPlans);
      })
      .catch((err) => setError(err?.message ?? 'Unable to load options'))
      .finally(() => setLoadingOptions(false));
  }, []);

  // Auto-fill site limit + expiry when plan changes
  useEffect(() => {
    if (!selectedPlan) return;
    setSiteLimit(selectedPlan.siteLimit === 999 ? '999' : String(selectedPlan.siteLimit));
    setExpiresAt(calcExpiry(selectedPlan.licenseDurationDays));
  }, [selectedPlan]);

  const handleCopy = () => {
    if (!generated) return;
    navigator.clipboard.writeText(generated.licenseKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!productId) { setError('Please select a product.'); return; }
    if (!userId) { setError('Please select a customer or choose yourself.'); return; }
    if (siteLimitNum < 1) { setError('Site limit must be at least 1.'); return; }

    try {
      setSaving(true);
      const result = await generateAdminLicense({
        productId,
        planId: planId || null,
        userId,
        siteLimit: siteLimitNum,
        expiresAt: expiresAt || null,
        status,
      });
      setGenerated(result);
    } catch (err) {
      setError((err as any)?.message ?? 'Unable to generate license');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAnother = () => {
    setGenerated(null);
    setProductId('');
    setPlanId('');
    setUserId('');
    setSiteLimit('1');
    setExpiresAt('');
    setStatus('active');
    setNotes('');
    setError('');
    setCopied(false);
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (generated) {
    return (
      <div className="max-w-2xl space-y-5">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Generate License</h1>
          <p className="text-sm text-surface-500 mt-1">Manually create a license key for a customer.</p>
        </div>

        <div className="bg-white rounded-xl border border-surface-200 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-2xl text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-surface-900 mb-1">License Generated</h2>
          <p className="text-sm text-surface-500 mb-5">{generated.productName} · ****{generated.licenseKeyLast4}</p>

          <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 mb-5 text-left">
            <p className="text-xs font-medium text-surface-500 mb-2">License Key — copy this now, it will not be shown again</p>
            <div className="flex items-center gap-3">
              <p className="flex-1 text-lg font-mono font-semibold text-surface-900 tracking-widest break-all">
                {generated.licenseKey}
              </p>
              <button
                onClick={handleCopy}
                className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${
                  copied
                    ? 'border-green-300 bg-green-50 text-green-600'
                    : 'border-surface-300 hover:bg-surface-100 text-surface-500'
                }`}
                title="Copy license key"
              >
                <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
              </button>
            </div>
            {copied && <p className="text-xs text-green-600 mt-2">Copied to clipboard!</p>}
          </div>

          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/admin/licenses')} className="px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors">
              View Licenses
            </button>
            <button onClick={handleGenerateAnother} className="px-5 py-2.5 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors">
              Generate Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Generate License</h1>
        <p className="text-sm text-surface-500 mt-1">Manually create a license key for a customer.</p>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-surface-200 p-5 md:p-6 space-y-5">

        {/* Product */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Product</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={loadingOptions}
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 disabled:bg-surface-100"
          >
            <option value="">{loadingOptions ? 'Loading…' : 'Select a product'}</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Plan */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Plan</label>
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            disabled={loadingOptions}
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 disabled:bg-surface-100"
          >
            <option value="">
              {loadingOptions ? 'Loading…' : plans.length === 0 ? 'No plans available' : 'Select a plan (optional)'}
            </option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — ${p.price} / {p.siteLimit === 999 ? 'Unlimited' : p.siteLimit} site{p.siteLimit !== 1 ? 's' : ''}{p.licenseDurationDays ? ` / ${p.licenseDurationDays}d` : ' / Lifetime'}
              </option>
            ))}
          </select>
          {selectedPlan && (
            <p className="mt-1.5 text-xs text-surface-500 flex flex-wrap gap-3">
              <span><i className="ri-global-line mr-1" />{selectedPlan.siteLimit === 999 ? 'Unlimited sites' : `${selectedPlan.siteLimit} site${selectedPlan.siteLimit !== 1 ? 's' : ''}`}</span>
              <span><i className="ri-time-line mr-1" />{selectedPlan.licenseDurationDays ? `${selectedPlan.licenseDurationDays} days` : 'Lifetime'}</span>
              <span><i className="ri-money-dollar-circle-line mr-1" />${selectedPlan.price} {selectedPlan.currency}</span>
            </p>
          )}
        </div>

        {/* Customer */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Assign To</label>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={loadingOptions}
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 disabled:bg-surface-100"
          >
            <option value="">{loadingOptions ? 'Loading…' : 'Select a recipient'}</option>
            {user && (
              <option value={user.id}>
                Myself — {profile?.name || user.email} (admin)
              </option>
            )}
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name} — {c.email}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-surface-400">Choose "Myself" to issue a license you'll use directly for a client.</p>
        </div>

        {/* Site limit + Expiry */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1.5">
              Site Limit
              {planMaxSites && planMaxSites !== 999 && (
                <span className="ml-2 text-xs font-normal text-surface-400">plan max: {planMaxSites}</span>
              )}
              {planMaxSites === 999 && (
                <span className="ml-2 text-xs font-normal text-surface-400">unlimited plan</span>
              )}
            </label>
            <input
              type="number"
              min="1"
              value={siteLimit}
              onChange={(e) => setSiteLimit(e.target.value)}
              className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 ${siteOverPlan ? 'border-amber-400 bg-amber-50' : 'border-surface-300'}`}
            />
            {siteOverPlan && (
              <p className="mt-1 text-xs text-amber-600">
                <i className="ri-error-warning-line mr-1" />
                Exceeds plan limit of {planMaxSites}. The license will still be created but may conflict with plan terms.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1.5">
              Expires At
              <span className="ml-2 text-xs font-normal text-surface-400">leave blank for lifetime</span>
            </label>
            <input
              type="date"
              value={expiresAt}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            />
            {selectedPlan?.licenseDurationDays && expiresAt && (
              <p className="mt-1 text-xs text-surface-400">Auto-set from plan ({selectedPlan.licenseDurationDays} days). You can override.</p>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as LicenseStatus)}
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive — generate now, activate later</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Internal Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            placeholder="e.g. issued for client project, comp license…"
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="submit"
            disabled={saving || loadingOptions}
            className="px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
          >
            {saving ? 'Generating…' : 'Generate License'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/licenses')}
            className="px-5 py-2.5 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
